import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Collapse,
  Chip,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FlagIcon from "@mui/icons-material/Flag";
import { getStatusChipProps } from "../../utils/statusChipUtils";

const ControlHeaderCard = ({
  control,
  currentStatusKey,
  redFlag,
  isLoading,
  controlDetailsExpanded,
  onToggleExpanded,
  isAuditorView = false,
}) => {
  const theme = useTheme();

  const statusProps = currentStatusKey ? getStatusChipProps(currentStatusKey, theme) : null;

  return (
    <Card
      sx={{
        mb: 3,
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        border: "1px solid #E8EAED",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {isLoading ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
              <Skeleton variant="text" width="30%" height={36} />
              <Skeleton variant="rectangular" width={80} height={28} sx={{ borderRadius: "6px" }} />
            </Box>
            <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1.5 }} />
            <Skeleton variant="text" width="90%" height={20} />
            <Skeleton variant="text" width="85%" height={20} />
          </>
        ) : (
          <>
            {/* Title Row with Control ID, Title, workflow status, expand */}
            <Box
              onClick={onToggleExpanded}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: controlDetailsExpanded ? 2.5 : 0,
                cursor: "pointer",
                borderRadius: "8px",
                p: 1,
                mx: -1,
                transition: "background-color 0.2s ease",
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: theme.customColors.text.strong,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <span>{control?.controlsName}</span>
                {control?.controlsTitle && (
                  <Typography
                    component="span"
                    sx={{
                      fontWeight: 400,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    - {control?.controlsTitle}
                  </Typography>
                )}
                {/* Status Chips Container */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {statusProps && (
                    <Chip
                      label={statusProps.label}
                      size="small"
                      icon={statusProps.icon}
                      sx={{
                        backgroundColor: statusProps.backgroundColor,
                        color: statusProps.color,
                        borderRadius: 0,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        border: "none",
                        "& .MuiChip-icon": {
                          color: statusProps.iconColor,
                          marginLeft: "8px",
                        },
                      }}
                    />
                  )}
                  {redFlag && (
                    <Chip
                      label="Red Flagged"
                      size="small"
                      icon={<FlagIcon fontSize="small" />}
                      sx={{
                        backgroundColor: theme.customColors.status.error.bg,
                        color: theme.customColors.status.error.text,
                        borderRadius: 0,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        border: "none",
                        "& .MuiChip-icon": {
                          color: theme.customColors.status.error.text,
                          marginLeft: "8px",
                        },
                      }}
                    />
                  )}
                </Box>
              </Typography>
              <Box
                sx={{
                  flexShrink: 0,
                  transform: controlDetailsExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  color: theme.customColors.text.secondary,
                }}
              >
                <ExpandMoreIcon />
              </Box>
            </Box>

            {/* Domain and Description Section - Collapsible */}
            <Collapse in={controlDetailsExpanded} timeout="auto">
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  pt: 2.5,
                  mt: 2.5,
                  borderTop: `1px solid ${theme.customColors.border.main}`,
                }}
              >
                {/* Domain Info */}
                {control?.controlsDomain && (
                  <Box sx={{ minWidth: "180px" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.customColors.text.secondary,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.75rem",
                        mb: 0.75,
                        display: "block",
                      }}
                    >
                      Domain
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.customColors.text.strong,
                        fontWeight: 500,
                      }}
                    >
                      {control.controlsDomain}
                    </Typography>
                  </Box>
                )}

                {/* Description */}
                {control?.controlsDescription && (
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.customColors.text.secondary,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.75rem",
                        mb: 0.75,
                        display: "block",
                      }}
                    >
                      Description
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.customColors.text.strong,
                        lineHeight: 1.6,
                      }}
                    >
                      {control.controlsDescription}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ControlHeaderCard;
